import { defineStore } from "pinia";
import * as yup from "yup";
import { ENUM_FIELD_LOCALE, ENUM_MUTATION, ENUM_MUTATION_STATUS } from "@/configs/enums/enum";
import type { typeGetAlert } from "@/configs/types/Alert/typeAlert";
import { fetchAlert } from "@/configs/fetches/PoliceCommandCenter/fetchAlert";
import { convertToValues, convertToNumber } from "@/configs/helpers/MainHelper";
import dayjs from "dayjs";
import { fetchFile } from "@/configs/fetches/PoliceCommandCenter/fetchFile";

const storeName = `useAlertFormStore-${import.meta.env.VITE_APP_VERSION}`;

const isRequired = {
  publishedAt: {
    is: true,
    name: `publishedAt`,
    label: `form.publishedAt.label`,
    placeholder: `form.publishedAt.placeholder`,
    value: null,
  },
  expiryAt: {
    is: true,
    name: `expiryAt`,
    label: `form.expiryAt.label`,
    placeholder: `form.expiryAt.placeholder`,
    value: null,
  },
  published: {
    is: true,
    name: `published`,
    label: `form.published.label`,
    placeholder: `form.published.placeholder`,
    value: false,
  },
  latitude: {
    is: false,
    name: `latitude`,
    label: `form.latitude.label`,
    placeholder: `form.latitude.placeholder`,
    value: null,
  },
  longitude: {
    is: false,
    name: `longitude`,
    label: `form.longitude.label`,
    placeholder: `form.longitude.placeholder`,
    value: null,
  },
  area: {
    is: false,
    name: `area`,
    label: `form.area.label`,
    placeholder: `form.area.placeholder`,
    value: null,
  },
  translations: ['TH', 'EN', 'CN', 'JP', 'KR', 'RU'].map((value, index) => ({
    locale: {
      is: value === 'TH',
      name: `content[${index}].locale`,
      label: `form.locales.label`,
      placeholder: `form.locales.placeholder`,
      value,
    },
    title: {
      is: value === 'TH',
      name: `content[${index}].title`,
      label: `form.title_alert.label`,
      placeholder: `form.title_alert.placeholder`,
      value: null,
    },
    description: {
      is: value === 'TH',
      name: `content[${index}].description`,
      label: `form.description.label`,
      placeholder: `form.description.placeholder`,
      value: null,
    },
    detail: {
      is: value === 'TH',
      name: `content[${index}].detail`,
      label: `form.detail.label`,
      placeholder: `form.detail.placeholder`,
      value: null,
    },
  })),
};
export const useAlertFormStore = defineStore(storeName, {
  state: () => ({
    fetchAlert: new fetchAlert(),
    fetchFile: new fetchFile(),
    results: [] as any,
    isOpen: false,
    isSuccess: false,
    isCancel: false,
    isDelete: false,
    loading: true,
    isRequired,
    action: null as ENUM_MUTATION | null,
    actionStatus: null as ENUM_MUTATION_STATUS | null,
    result: convertToValues(isRequired) as typeGetAlert,
    error: {} as { [key: string]: string | null },
    validationSchema: yup.object({
      published: isRequired.published.is ? yup.boolean().nullable().required() : yup.boolean().nullable().notRequired(),
      publishedAt: isRequired.publishedAt.is
        ? yup
          .string()
          .nullable()
          .transform((value, originalValue) => {
            return originalValue === "" ? dayjs().tz("Asia/Bangkok").toISOString() : dayjs(value).tz("Asia/Bangkok").toISOString();
          })
          .required()
        : yup
          .string()
          .nullable()
          .transform((value, originalValue) => {
            return !originalValue?.length ? dayjs().tz("Asia/Bangkok").toISOString() : dayjs(value).tz("Asia/Bangkok").toISOString();
          })
          .notRequired(),
      expiryAt: isRequired.expiryAt.is
        ? yup
          .string()
          .nullable()
          .transform((value, originalValue) => {
            return !originalValue?.length ? dayjs().tz("Asia/Bangkok").toISOString() : dayjs(value).tz("Asia/Bangkok").toISOString();
          })
          .required()
        : yup
          .string()
          .nullable()
          .transform((value, originalValue) => {
            return !originalValue?.length ? dayjs().tz("Asia/Bangkok").toISOString() : dayjs(value).tz("Asia/Bangkok").toISOString();
          })
          .notRequired(),
      latitude: isRequired.latitude.is ? yup.number().nullable().required() : yup.number().nullable().notRequired(),
      longitude: isRequired.longitude.is ? yup.number().nullable().required() : yup.number().nullable().notRequired(),
      area: isRequired.area.is ? yup.number().nullable().required() : yup.number().nullable().notRequired(),
      translations: yup.array().of(
        yup.lazy((value) => {
          if (value && value.locale === ENUM_FIELD_LOCALE.TH) {
            return yup.object().shape({
              locale: isRequired.translations[0].locale.is ? yup.string().nullable().required() : yup.string().nullable().notRequired(),
              title: isRequired.translations[0].title.is ? yup.string().nullable().required() : yup.string().nullable().notRequired(),
              description: isRequired.translations[0].description.is ? yup.string().nullable().required() : yup.string().nullable().notRequired(),
            });
          } else if (value && value.locale === ENUM_FIELD_LOCALE.EN) {
            return yup.object().shape({
              locale: isRequired.translations[1].locale.is ? yup.string().nullable().required() : yup.string().nullable().notRequired(),
              title: isRequired.translations[1].title.is ? yup.string().nullable().required() : yup.string().nullable().notRequired(),
              description: isRequired.translations[1].description.is ? yup.string().nullable().required() : yup.string().nullable().notRequired(),
            });
          } else if (value && value.locale === ENUM_FIELD_LOCALE.CN) {
            return yup.object().shape({
              locale: isRequired.translations[2].locale.is ? yup.string().nullable().required() : yup.string().nullable().notRequired(),
              title: isRequired.translations[2].title.is ? yup.string().nullable().required() : yup.string().nullable().notRequired(),
              description: isRequired.translations[2].description.is ? yup.string().nullable().required() : yup.string().nullable().notRequired(),
            });
          } else if (value && value.locale === ENUM_FIELD_LOCALE.JP) {
            return yup.object().shape({
              locale: isRequired.translations[3].locale.is ? yup.string().nullable().required() : yup.string().nullable().notRequired(),
              title: isRequired.translations[3].title.is ? yup.string().nullable().required() : yup.string().nullable().notRequired(),
              description: isRequired.translations[3].description.is ? yup.string().nullable().required() : yup.string().nullable().notRequired(),
            });
          } else if (value && value.locale === ENUM_FIELD_LOCALE.KR) {
            return yup.object().shape({
              locale: isRequired.translations[4].locale.is ? yup.string().nullable().required() : yup.string().nullable().notRequired(),
              title: isRequired.translations[4].title.is ? yup.string().nullable().required() : yup.string().nullable().notRequired(),
              description: isRequired.translations[4].description.is ? yup.string().nullable().required() : yup.string().nullable().notRequired(),
            });
          } else if (value && value.locale === ENUM_FIELD_LOCALE.RU) {
            return yup.object().shape({
              locale: isRequired.translations[5].locale.is ? yup.string().nullable().required() : yup.string().nullable().notRequired(),
              title: isRequired.translations[5].title.is ? yup.string().nullable().required() : yup.string().nullable().notRequired(),
              description: isRequired.translations[5].description.is ? yup.string().nullable().required() : yup.string().nullable().notRequired(),
            });
          }
          return yup.object();
        })
      ),
    }),
  }),
  actions: {
    async onDeleteImageSubmit() {
      return await this.update(this.result);
    },
    async onSubmit() {
      console.log('onSubmit')
      const keysToConvert: string[] = [];
      this.result = convertToNumber(this.result, keysToConvert);

      Object.keys(this.error).forEach((key) => {
        this.error[key] = null;
      });

      try {
        await this.validationSchema.validate(this.result, {
          abortEarly: false,
        });
        if (!this.result.id?.length) {
          return await this.create(this.result);
        } else {
          return await this.update(this.result);
        }
      } catch (error: any) {

        console.log(error)
        // (error.inner || []).map((inner: any) => {
        //   if (inner.message.length) {
        //     this.error[inner.path] = inner.message;
        //   }
        // });
      }
    },
    async create(result: typeGetAlert) {
      try {
        this.loading = true;
        const data = await this.fetchAlert.Create(result);
        this.actionStatus = ENUM_MUTATION_STATUS.SUCCESS;
        this.loading = false;

        this.isSuccess = true;
        return data;
      } catch (error: any) {
        this.actionStatus = ENUM_MUTATION_STATUS.ERROR;
        this.loading = false;

        this.isSuccess = true;
        throw error;
      }
    },
    async update(result: typeGetAlert): Promise<typeGetAlert> {
      console.log('update')
      try {
        this.loading = true;
        const data = await this.fetchAlert.Update(result.id || "", result);
        this.actionStatus = ENUM_MUTATION_STATUS.SUCCESS;
        this.loading = false;

        this.isSuccess = true;
        return data;
      } catch (error: any) {
        this.actionStatus = ENUM_MUTATION_STATUS.ERROR;
        this.loading = false;

        this.isSuccess = true;
        throw error;
      }
    },
  },
});
